import React, { useState } from "react";
import {
  Box,
  Button,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
} from "@mui/material";
import { AddCircleOutline, RemoveCircleOutline } from "@mui/icons-material";

const MAX_VISIBLE_FILTER_ROWS = 4;
const FILTER_ROW_HEIGHT = 56;

const initialFilters = [{ columnField: "", operatorValue: "equals", value: "" }];

const FilterComponent = ({ columns, onApplyFilters }) => {
  const [filters, setFilters] = useState(initialFilters);
  const [logicOperator, setLogicOperator] = useState("AND");

  const filterableColumns = columns.filter(
    (column) => column.field && column.field !== "actions"
  );

  const handleAddFilter = () => {
    setFilters([
      ...filters,
      { columnField: "", operatorValue: "equals", value: "" },
    ]);
  };

  const handleRemoveFilter = (index) => {
    if (filters.length === 1) {
      setFilters(initialFilters);
      return;
    }
    setFilters(filters.filter((_, i) => i !== index));
  };

  const handleFilterChange = (index, field, value) => {
    const newFilters = [...filters];
    newFilters[index][field] = value;
    setFilters(newFilters);
  };

  const handleApplyFilters = () => {
    onApplyFilters(filters, logicOperator);
  };

  const getColumnLabel = (field) =>
    filterableColumns.find((column) => column.field === field)?.headerName || field;

  return (
    <Box sx={{ width: "100%" }}>
      <FormControl
        variant="outlined"
        size="small"
        sx={{ width: 160, mb: 1.5, mt: 0.5 }}
      >
        <InputLabel id="filter-logic-operator-label">Logic Operator</InputLabel>
        <Select
          labelId="filter-logic-operator-label"
          value={logicOperator}
          onChange={(e) => setLogicOperator(e.target.value)}
          label="Logic Operator"
        >
          <MenuItem value="AND">AND</MenuItem>
        </Select>
      </FormControl>

      <Box
        sx={{
          maxHeight: MAX_VISIBLE_FILTER_ROWS * FILTER_ROW_HEIGHT + 12,
          overflowY: filters.length > MAX_VISIBLE_FILTER_ROWS ? "auto" : "visible",
          overflowX: "hidden",
          pt: 1,
          pb: 0.5,
          pr: filters.length > MAX_VISIBLE_FILTER_ROWS ? 0.5 : 0,
          mb: 1.5,
        }}
      >
        {filters.map((filter, index) => (
          <Grid
            container
            spacing={1}
            alignItems="flex-end"
            key={`filter-row-${index}`}
            sx={{ mb: 1.25, minHeight: 48, overflow: "visible" }}
          >
            <Grid item xs={4} sx={{ overflow: "visible" }}>
              <FormControl fullWidth size="small" variant="outlined">
                <InputLabel id={`filter-column-label-${index}`} shrink>
                  Column
                </InputLabel>
                <Select
                  labelId={`filter-column-label-${index}`}
                  value={filter.columnField}
                  onChange={(e) =>
                    handleFilterChange(index, "columnField", e.target.value)
                  }
                  label="Column"
                  displayEmpty
                  notched
                  renderValue={(selected) =>
                    selected ? (
                      getColumnLabel(selected)
                    ) : (
                      <Box component="span" sx={{ color: "text.secondary" }}>
                        Select column
                      </Box>
                    )
                  }
                >
                  {filterableColumns.map((column) => (
                    <MenuItem key={column.field} value={column.field}>
                      {column.headerName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={3} sx={{ overflow: "visible" }}>
              <FormControl fullWidth size="small" variant="outlined">
                <InputLabel id={`filter-operator-label-${index}`} shrink>
                  Operator
                </InputLabel>
                <Select
                  labelId={`filter-operator-label-${index}`}
                  value={filter.operatorValue}
                  onChange={(e) =>
                    handleFilterChange(index, "operatorValue", e.target.value)
                  }
                  label="Operator"
                  notched
                >
                  <MenuItem value="equals">Equals</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={4} sx={{ overflow: "visible" }}>
              <TextField
                fullWidth
                size="small"
                variant="outlined"
                label="Value"
                value={filter.value}
                onChange={(e) => handleFilterChange(index, "value", e.target.value)}
                InputLabelProps={{ shrink: true }}
                placeholder="Enter value"
              />
            </Grid>

            <Grid
              item
              xs={1}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <IconButton
                size="small"
                onClick={() => handleRemoveFilter(index)}
                aria-label="Remove filter"
              >
                <RemoveCircleOutline fontSize="small" />
              </IconButton>
            </Grid>
          </Grid>
        ))}
      </Box>

      <Stack direction="row" spacing={1} alignItems="center">
        <Button
          variant="outlined"
          size="small"
          onClick={handleAddFilter}
          startIcon={<AddCircleOutline />}
        >
          Add Filter
        </Button>
        <Button variant="contained" size="small" onClick={handleApplyFilters}>
          Apply Filters
        </Button>
      </Stack>
    </Box>
  );
};

export default FilterComponent;
